import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, ShieldCheck, CheckCircle2, User, RefreshCw } from 'lucide-react';
import { StarRating } from '../common/StarRating';
import { fetchProfessionalReviewsApi } from '../../lib/supabaseService';

interface ProfessionalReviewsSectionProps {
  professionalId: string;
  professionalName?: string;
  initialRating?: number;
  initialReviewCount?: number;
}

export const ProfessionalReviewsSection: React.FC<ProfessionalReviewsSectionProps> = ({
  professionalId,
  professionalName = 'Artisan',
  initialRating = 5.0,
  initialReviewCount = 0,
}) => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [averageRating, setAverageRating] = useState<number>(initialRating);
  const [totalReviews, setTotalReviews] = useState<number>(initialReviewCount);
  const [breakdown, setBreakdown] = useState<Record<number, number>>({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadReviews = async () => {
    if (!professionalId) return;
    setIsLoading(true);
    try {
      const res = await fetchProfessionalReviewsApi(professionalId);
      if (res.success) {
        setReviews(res.reviews || []);
        if (res.totalReviews > 0) {
          setAverageRating(res.averageRating);
          setTotalReviews(res.totalReviews);
          setBreakdown(res.breakdown || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
        }
      }
    } catch (err) {
      console.warn('Error loading professional reviews:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, [professionalId]);

  return (
    <div className="bg-white rounded-3xl p-5 sm:p-7 border border-slate-200/80 shadow-xs space-y-6">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
        <div className="space-y-1">
          <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            <span>Customer Reviews & Verified Ratings</span>
          </h3>
          <p className="text-xs text-slate-500">
            Real feedback from verified Nigerian customers upon job completion
          </p>
        </div>

        <button
          type="button"
          onClick={loadReviews}
          disabled={isLoading}
          className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold flex items-center gap-1.5 self-start sm:self-auto cursor-pointer"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Aggregate Rating Overview Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 items-center p-5 bg-slate-50 rounded-2xl border border-slate-200/70">
        {/* Left Big Rating Score */}
        <div className="sm:col-span-4 text-center sm:border-r border-slate-200/80 sm:pr-6 space-y-2">
          <div className="text-4xl sm:text-5xl font-black text-slate-900">
            {Number(averageRating).toFixed(1)}
          </div>
          <StarRating value={averageRating} readOnly size="lg" className="justify-center" />
          <p className="text-xs font-bold text-slate-500">
            Based on {totalReviews} verified {totalReviews === 1 ? 'review' : 'reviews'}
          </p>
        </div>

        {/* Right Star Breakdown Bars */}
        <div className="sm:col-span-8 space-y-2 text-xs">
          {[5, 4, 3, 2, 1].map((starNum) => {
            const count = breakdown[starNum] || 0;
            const percentage = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : starNum === 5 ? 100 : 0;

            return (
              <div key={starNum} className="flex items-center gap-2.5">
                <span className="w-12 text-slate-600 font-bold shrink-0 flex items-center gap-1">
                  <span>{starNum}</span>
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                </span>

                <div className="flex-1 h-2.5 bg-slate-200/80 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  />
                </div>

                <span className="w-10 text-right text-slate-400 font-medium text-[11px] shrink-0">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reviews Cards List */}
      <div className="space-y-4">
        {isLoading && reviews.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-400">Loading reviews...</div>
        ) : reviews.length === 0 ? (
          <div className="py-8 text-center bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
            <MessageSquare className="w-8 h-8 text-slate-300 mx-auto" />
            <h4 className="text-xs font-bold text-slate-700">No Customer Reviews Yet</h4>
            <p className="text-[11px] text-slate-400 max-w-sm mx-auto">
              This artisan is verified and active. Customer reviews appear automatically after completed jobs.
            </p>
          </div>
        ) : (
          reviews.map((rev) => (
            <div
              key={rev.id}
              className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200/80 space-y-3 shadow-2xs"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <img
                    src={rev.customerAvatar}
                    alt={rev.customerName}
                    className="w-10 h-10 rounded-xl object-cover ring-2 ring-slate-100 shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h4 className="text-xs font-black text-slate-900">{rev.customerName}</h4>
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200/60 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                        Verified Job
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      {rev.jobTitle} • {rev.categoryName}
                    </p>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <StarRating value={rev.rating} readOnly size="sm" />
                  <span className="text-[10px] text-slate-400 block mt-1">
                    {new Date(rev.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>

              {rev.comment && (
                <p className="text-xs text-slate-700 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">
                  "{rev.comment}"
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
